import {t, useSiteLanguage} from "../../i18n";
import {ReactNode} from "react";
import restaurantInfo from "../../data/restaurant.json";
import {useReservationBooking} from "../../context/ReservationBookingContext";

type ReservationButtonProps = {
    children?: ReactNode;
    className?: string;
    onClick?: () => void;
    /** La surface sur laquelle il est posé, non son apparence : c'est elle qui décide. */
    variant?: 'surface' | 'on-image';
};

/**
 * Le bouton « Réserver », visible immédiatement et jamais mort.
 *
 * C'est **toujours** un lien téléphonique. Trois conséquences, et chacune corrige un défaut :
 *
 *   - il s'affiche dès le premier rendu, sans attendre que le widget réponde ;
 *   - il fonctionne sans JavaScript, et si le widget ne charge jamais ;
 *   - il ne change pas de nature sous le doigt du visiteur. Une version antérieure passait de
 *     `<a>` à `<button>` quand le widget devenait prêt : un changement de comportement au
 *     milieu de la page, invisible mais réel.
 *
 * Quand la réservation en ligne est active, l'attribut `data-restaurant-booking` le désigne, et
 * le script du widget intercepte le clic.
 *
 * ## Le libellé ne bouge plus
 *
 * Il basculait sur « Appeler le restaurant » après trois secondes d'attente. C'était le seul
 * canal disponible tant que rien ne s'affichait au clic — et il avait son coût : un libellé qui
 * change sous les yeux, sur tous les boutons de la page à la fois, pour un visiteur qui n'avait
 * peut-être rien demandé.
 *
 * Le panneau d'attente a pris ce rôle. Il n'apparaît qu'après un clic, il ne concerne que ce
 * clic, et il porte le secours téléphonique explicitement. Le libellé passé en `children` est
 * donc conservé tel quel : chaque endroit de la page a le sien — « Réserver », « Réserver une
 * table ».
 */
const ReservationButton = ({
    children = t("booking.reserve"),
    className,
    onClick,
    variant = 'surface',
}: ReservationButtonProps) => {
    useSiteLanguage();
    const {shouldOpenWidget} = useReservationBooking();

    return (
        <a
            href={`tel:${restaurantInfo.fixNumber}`}
            className={`ui-button${variant === 'on-image' ? ' ui-button--on-image' : ''} ${className ?? ''}`.trim()}
            onClick={onClick}
            {...(shouldOpenWidget ? {"data-restaurant-booking": true} : {})}
        >
            {children}
        </a>
    );
};

export default ReservationButton;
